import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Search from "../../../components/Search";
import { setCurrentPage, setSearchText } from "../../../redux/historicalSlice";
import { setFilterByCurrentUser } from "../../../redux/filtersSlice";
import { RootState } from "../../../redux/store";
import AppTable, { ColumnConfig } from "../../../components/AppTable/AppTable";
import { ImprovementPlanListItemDto } from "../types";
import { SectionPermissionTypeEnum } from "../../../types/SectionPermissionTypesEnum";
import { formatDate } from "../../../utils/formatters/DateFormatter";
import AlertMessage from "../../../components/AlertMessage";
import { useAlert } from "../../../hooks/useAlert";
import {
    createProposedPlansFromAI,
    deleteLogicImprovementPlan,
    exportImprovementPlansToCsv,
    getImprovementPlanDeleteReasonOptions
} from "../api/improvementPlanAPI";
import ImprovementPlanFinalizeModal from "../components/modals/ImprovementPlanFinalizeModal";
import ImprovementPlanDeleteModal from "../components/modals/ImprovementPlanDeleteModal";
import { useImprovementPlans } from "../hooks/useImprovementPlans";
import { SelectItemDto } from "../../../types/SelectItemDto";
import { useModal } from "../../../hooks/useModal";
import { ApiErrorResponse } from "../../../types/ApiErrorResponse";
import { useQueryClient } from "@tanstack/react-query";

export default function ImprovementPlansList() {
    const { t } = useTranslation(["improvementPlan", "common"]);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const currentPage = useSelector((state: RootState) => state.historical.improvementPlans.currentPage);
    const itemsPerPage = useSelector((state: RootState) => state.historical.improvementPlans.itemsPerPage);
    const searchText = useSelector((state: RootState) => state.historical.improvementPlans.searchText);

    const assignedToMeToggle = useSelector((state: RootState) => state.filters.improvementPlans.filterByCurrentUser);
    const queryClient = useQueryClient();

    const [showEndModal, setShowEndModal] = useState(false);
    const [selectedImprovementPlanId, setSelectedImprovementPlanId] = useState<number | null>(null);
    const [deleteReasonOptions, setDeleteReasonOptions] = useState<SelectItemDto[]>([]);
    const [selectedDeleteReasonId, setSelectedDeleteReasonId] = useState<number | null>(null);
    const [deleteModalError, setDeleteModalError] = useState<string | undefined>(undefined);
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [isLoadingDeleteReasons, setIsLoadingDeleteReasons] = useState(false);
    const {
        showModal: showDeleteModal,
        modalData: deleteModalData,
        openModal: openDeleteModal,
        closeModal: closeDeleteModalBase,
        currentItem: selectedDeleteItem,
        isLoading: isDeleting,
        setLoading: setIsDeleting
    } = useModal<ImprovementPlanListItemDto>();

    const { data: improvementPlansPagination, isLoading: isListLoading, error, refetch } = useImprovementPlans({});

    useEffect(() => {
        if (location.state?.shouldRefresh) {
            refetch();
        }
    }, [location.state, refetch]);

    const handleSearchInput = () => {
        dispatch(setCurrentPage({ table: "improvementPlans", page: 1 }));
    };

    const handleClear = () => {
        dispatch(setSearchText({ table: "improvementPlans", text: "", goFirstPage: true }));
    };

    const columns: ColumnConfig<ImprovementPlanListItemDto>[] = [
        { header: t("common:form.fields.id"), key: "id", render: (item) => <b>{item.id}</b> },
        {
            header: t("common:form.fields.title"),
            key: "title",
            headerClassName: "text-align-left",
            cellStyle: { textAlign: "left", minWidth: "480px" },
        },
        { header: t("improvementPlan:table.type"), key: "type" },
        { header: t("common:form.fields.startDate"), key: "startDate", render: (item) => <>{formatDate(item.startDate)}</> },
        { header: t("common:form.fields.endDate"), key: "endDate", render: (item) => <>{formatDate(item.endDate)}</> },
        { header: t("common:form.fields.status"), key: "status", render: (item) => <>{t(`improvementPlan:form.status.${item.status}`)}</> },
        { header: t("improvementPlan:table.claims"), key: "claims", render: (item) => <>{`${item.activeDemands} / ${item.totalDemands}`}</> },
        { header: t("improvementPlan:table.improvements"), key: "improvements", render: (item) => <>{`${item.activeActions} / ${item.totalActions}`}</> },
    ];

    const { alert, hideAlert, isVisible, showAlert } = useAlert({ fromLocation: true });
    const IMPROVEMENT_PLAN_MODAL_HEADERS = {
        titleDelete: t("improvementPlan:modal.improvementPlanModalHeaderDeleteTitle"),
        subtitleDelete: t("improvementPlan:modal.improvementPlanModalHeaderDeleteSubtitle"),
    };

    const handleDelete = async (improvementPlan: ImprovementPlanListItemDto) => {
        openDeleteModal(
            improvementPlan,
            IMPROVEMENT_PLAN_MODAL_HEADERS.titleDelete,
            `${IMPROVEMENT_PLAN_MODAL_HEADERS.subtitleDelete}: ${improvementPlan.title}?`,
            "deleteWithoutInput",
            ""
        );
        setSelectedDeleteReasonId(null);
        setDeleteModalError(undefined);
        setIsLoadingDeleteReasons(true);

        try {
            const options = await getImprovementPlanDeleteReasonOptions();
            setDeleteReasonOptions(options);
        } catch (error) {
            console.log(error);
            setDeleteModalError(t("common:requestError.errorBadRequest"));
        } finally {
            setIsLoadingDeleteReasons(false);
        }
    };

    const handleOpenEndModal = (improvementPlan: ImprovementPlanListItemDto) => {
        setShowEndModal(true);
        setSelectedImprovementPlanId(improvementPlan.id);
    };

    const handleCloseEndModal = () => {
        setShowEndModal(false);
        setSelectedImprovementPlanId(null);
    };

    const hadleAssignedToMe = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setFilterByCurrentUser({ table: "improvementPlans", filterByCurrentUser: e.target.checked }));
    };

    const closeDeleteModal = () => {
        closeDeleteModalBase();
        setSelectedDeleteReasonId(null);
        setDeleteModalError(undefined);
        setDeleteReasonOptions([]);
    };

    const confirmDelete = async () => {
        if (!selectedDeleteItem) {
            return;
        }

        if (!selectedDeleteReasonId) {
            setDeleteModalError(t("common:form.requiredFieldForm"));
            return;
        }

        const selectedReason = deleteReasonOptions.find((option) => option.id === selectedDeleteReasonId);
        if (!selectedReason) {
            setDeleteModalError(t("common:requestError.errorBadRequest"));
            return;
        }

        setIsDeleting(true);

        try {
            await deleteLogicImprovementPlan(selectedDeleteItem.id, {
                deleteReasonId: selectedReason.id
            });
            closeDeleteModal();
            queryClient.invalidateQueries({
                queryKey: ['getImprovementPlanIndicators'],
                exact: false,
                refetchType: 'all',
            });
            queryClient.invalidateQueries({
                queryKey: ['improvementPlanPieChart'],
                exact: false,
                refetchType: 'all',
            });
            queryClient.invalidateQueries({
                queryKey: ['getImprovementPlanStatusAndTypeBarChart'],
                exact: false,
                refetchType: 'all',
            });
            navigate("/improvement-plans", {
                state: { message: t("improvementPlan:modal.messageDeletedImprovementPlanSuccessfully"), shouldRefresh: true }
            });
        } catch (error) {
            console.log(error);
            setDeleteModalError(t("common:requestError.errorBadRequest"));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleExportCsv = async () => {
        try {
            await exportImprovementPlansToCsv({
                pageNumber: 1,
                pageSize: 10,
                search: searchText,
                filterByCurrentUser: assignedToMeToggle,
                selectedOrganizationalStructureIds: []
            });
            showAlert({
                message: t("improvementPlan:exportCsvSuccess"),
                type: "success"
            });
        } catch (error) {
            showAlert({
                message: t("common:requestError.errorBadRequest"),
                type: "danger"
            });
        }
    };

    const generateImprovementPlanFromIA = () => {
        setIsPageLoading(true);
        createProposedPlansFromAI()
            .then(() => {
                showAlert({
                    message: t("improvementPlan:messages.proposedPlanFromAISuccessfully"),
                    type: "success",
                    duration: 5000
                });
                refetch();
            })
            .catch((error: ApiErrorResponse) => {
                showAlert({
                    message: error.message ? t(error.message) : t("common:requestError.errorBadRequest"),
                    type: "danger"
                });
            }).finally(() => setIsPageLoading(false));
    }

    return (
        <>
            <ImprovementPlanFinalizeModal
                show={showEndModal}
                improvementPlanId={selectedImprovementPlanId}
                onClose={handleCloseEndModal}
            />
            <ImprovementPlanDeleteModal
                show={showDeleteModal}
                title={deleteModalData.title}
                subtitle={deleteModalData.subtitle}
                isLoadingOptions={isLoadingDeleteReasons}
                isDeleting={isDeleting}
                options={deleteReasonOptions}
                selectedReasonId={selectedDeleteReasonId}
                error={deleteModalError}
                onClose={closeDeleteModal}
                onConfirm={confirmDelete}
                onReasonChange={(reasonId) => {
                    setSelectedDeleteReasonId(reasonId);
                    setDeleteModalError(undefined);
                }}
            />

            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {error && (
                        <div className="alert alert-danger">{t("common:requestError.errorBadRequest")}</div>
                    )}
                </Col>
            </Row>
            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}
                        />
                    )}
                </Col>
            </Row>
            <Row>
                <Col xs={12} md={12}>
                    <Search
                        searchText={searchText || ""}
                        onChange={(text) => dispatch(setSearchText({ table: "improvementPlans", text }))}
                        onSearch={handleSearchInput}
                        onClear={handleClear}
                    />
                </Col>
            </Row>

            <Row className="d-flex align-items-center">
                <Col md={6}>                
                    <Form>
                        <Form.Check
                            className="my-4"
                            type="switch"
                            id="improvement-plans-active-switch"
                            label={t("common:buttons.toggleAssignUser")}
                            onChange={hadleAssignedToMe}
                            checked={assignedToMeToggle}
                        />
                    </Form>
                </Col>
                <Col md={6} className="text-end d-none">
                    <Button onClick={generateImprovementPlanFromIA}>
                        {t("improvementPlan:proposePlan")}
                    </Button>
                </Col>
            </Row>

            <AppTable<ImprovementPlanListItemDto>
                data={improvementPlansPagination?.items || []}
                columns={columns}
                emptyMessage={t("improvementPlan:emptyMessage")}
                t={t}
                hasActions={true}
                hasPagination={true}
                currentPage={currentPage}
                totalPages={improvementPlansPagination?.totalPages || 0}
                pageSize={itemsPerPage}
                paginationType="improvementPlans"
                sectionPermission={SectionPermissionTypeEnum.IMPROVEMENT_PLANS}
                onView={(id: number) => { navigate(`/improvement-plans/${id}`); }}
                onExportCsv={handleExportCsv}
                onDelete={handleDelete}
                onEnd={handleOpenEndModal}
            />

            {(isListLoading || isPageLoading) && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
        </>
    );
}
